import type { ElasticQueryFilters } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import type {
  WorkJourneyConfigElasticSearchIdSource,
  WorkJourneySearchIdsResult,
} from '@src/models/interfaces/workJourneyConfig.interface';
import { escapeQueryStringValue } from '@src/util/elastic/escapeQueryStringValue';

export interface WorkJourneyListElasticFiltersParams {
  searchTerm?: string;
  exactMatch?: boolean;
  searchFields?: string[];
  extraFilters?: Partial<ElasticQueryFilters>;
}

const buildPartialMatch = (fields: string[], value: string): string => {
  const tokenClauses = value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(token => {
      const escaped = escapeQueryStringValue(token);
      const perField = fields.map(field => `${field}:*${escaped}*`);
      return perField.length === 1 ? perField[0] : `(${perField.join(' OR ')})`;
    });

  if (!tokenClauses.length) return '';
  return tokenClauses.length === 1 ? tokenClauses[0] : `(${tokenClauses.join(' AND ')})`;
};

export class WorkJourneyConfigMapper {
  static toListElasticFilters({
    searchTerm = '',
    exactMatch = false,
    searchFields = ['name', 'description'],
    extraFilters = {},
  }: WorkJourneyListElasticFiltersParams = {}): ElasticQueryFilters {
    const filters: ElasticQueryFilters = { ...extraFilters };
    const trimmedSearch = searchTerm.trim();
    const fields = searchFields.length > 0 ? searchFields : ['name'];

    if (trimmedSearch) {
      const escapedSearch = escapeQueryStringValue(trimmedSearch);
      filters.textSearch = exactMatch
        ? `(${fields.map(field => `${field}:"${escapedSearch}"`).join(' OR ')})`
        : buildPartialMatch(fields, trimmedSearch);
    }

    return filters;
  }

  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<WorkJourneyConfigElasticSearchIdSource>,
  ): string | null => {
    const id = hit._source?.id ?? hit._id;
    return id || null;
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<WorkJourneyConfigElasticSearchIdSource> | null,
  ): WorkJourneySearchIdsResult => {
    const hits = response?.hits?.hits ?? [];
    const ids = hits
      .map(hit => WorkJourneyConfigMapper.fromElasticSearchIdHit(hit))
      .filter((id): id is string => id != null);

    return {
      ids,
      total: response?.hits?.total?.value ?? ids.length,
    };
  };
}

export default WorkJourneyConfigMapper;
